import * as angular from 'angular';
import * as _ from 'lodash';

import { AggregationColumn, Dashboard, IsEndDateIncluded, MaxNumberOfItems, MaxPeriod, MinPeriod, NameConfiguration, PeriodReference, RowSpacingSelect, ShowLimit, SortBy, SortOrder } from '../../../common/helpers/contextual/predefinedContextualFields';
import { ComponentDefinition, ContextualField, DateOrder, Dictionary, IValueViewModel, KeyValuePair, Limit, NameBuilderConfiguration, PropertyType } from '../../../models/index';
import { PivotTableConfiguration } from '../pivotTable.configuration';
import { INameBuilderService } from '@services/widgets';

const defaultSortBy = [{ key: '', value: 'Row series' }];

class PivotTableContextualController implements angular.IComponentController {

    configuration: PivotTableConfiguration;
    formController: angular.IFormController;
    properties: ContextualField[];
    cancel: () => void;
    save: () => void;

    readonly viewModels: IValueViewModel[];

    private _originalConfiguration: PivotTableConfiguration;

    static $inject = [
        '$scope',
        '$q',
        'nameBuilderService'
    ];
    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _nameBuilderService: INameBuilderService
    ) {
    }

    $onInit() {
        const sortBy = angular.copy(SortBy);
        sortBy.extraData = { options: this.getSortByOptions(this.configuration) };
        const showLimit = angular.copy(ShowLimit);
        const maxItemsProperty = angular.copy(MaxNumberOfItems);
        showLimit.onChange = (viewValue, extraArgs) => maxItemsProperty.hide = viewValue === 0;
        maxItemsProperty.hide = !this.configuration?.limit;

        if (this.configuration) {
            this._$scope.$watchCollection(() => this.configuration.series, (oldValue, newValue) => {
                if (!!this.viewModels && !!this.properties) {
                    this.updateSortByOptions();
                }
            });
        }

        this.properties = [
            angular.copy(RowSpacingSelect),
            angular.copy(MinPeriod),
            angular.copy(MaxPeriod),
            angular.copy(PeriodReference),
            angular.copy(IsEndDateIncluded),
            angular.copy(AggregationColumn),
            angular.copy(Dashboard),

            angular.copy(NameConfiguration.HasGroupName),
            angular.copy(NameConfiguration.HasName),
            angular.copy(NameConfiguration.HasShortComment),
            angular.copy(NameConfiguration.HasUnit),
            angular.copy(NameConfiguration.HasValueType),
            sortBy,
            angular.copy(SortOrder),
            showLimit,
            maxItemsProperty
        ];
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['configuration']) {
            this._originalConfiguration = angular.copy(this.configuration);
            if (this.properties) {
                const found = _.find(this.properties, p => p.propertyName === 'maxItemsProperty');
                if (found) {
                    found.hide = !this.configuration?.limit;
                }
                this.updateSortByOptions();
            }
        }
        if (changesObj['viewModels'] && this.properties && this.configuration) {
            this.updateSortByOptions();
        }
    }

    onSave(): angular.IPromise<void> {
        return this._$q.when(this.save());
    }

    onCancel() {
        this.configuration = angular.copy(this._originalConfiguration);
        this.cancel();
    }

    private updateSortByOptions() {
        const found = _.find(this.properties, p => p.propertyName === 'sortByProperty');
        if (found) {
            const sortByOptions = this.getSortByOptions(this.configuration);
            if (sortByOptions.map(d => d.key).indexOf(this.configuration.sortBy) < 0) {
                this.configuration.sortBy = '';
            }
            found.extraData.options = sortByOptions;
        }
    }

    private getSortByOptions(configuration: PivotTableConfiguration): KeyValuePair<string, string>[] {
        let sortByOptions = [].concat(defaultSortBy);
        if (configuration && configuration.series && this.viewModels) {
            const nameConfiguration: NameBuilderConfiguration = {
                hasGroupName: false,
                hasName: true,
                hasValueType: true,
                hasShortComment: false,
                hasUnit: false,
            };
            sortByOptions = sortByOptions.concat(configuration.series.sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1)
                .map(d => {
                    const vm = _.find(this.viewModels, vm => vm.guid === d.guid);
                    const name = !!vm ?
                        this._nameBuilderService.getName(vm,
                            nameConfiguration,
                            d as any)
                        : 'Not found';
                    return { key: d.guid + d.value, value: name };
                }));
        }
        return sortByOptions;
    }
}

export const wtPivotTableContextual: ComponentDefinition = {
    componentId: 'wtPivotTableContextual',
    controller: PivotTableContextualController,
    template: require('./pivotTableContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        configuration: '<',
        formController: '=',
        save: '&?',
        cancel: '&?',
        viewModels: '<?'
    }
};